import * as React from "react";
import { useState, useMemo } from "react";
import { Controller, UseFormReturn } from "react-hook-form";
import { Plus, Search, Users, ShieldAlert, Info } from "lucide-react";

import { Field, Combobox, Button, Input, Badge } from "@/components/ui";
import { Dialog } from "@/components/ui/dialog";
import { useSegmentation, useDebounced } from "@/utils";
import { AssistantCoachServices } from "@/services/bedrock/AssistantCoachServices";
import { AssistantCoach } from "@/services/bedrock/dtos/AssistantCoach";
import { PlayersListWithBranch } from "@/components/Lists/PlayerBranchList";
import { cn } from "@/lib/utils";

interface AssignmentsSectionProps {
  form: UseFormReturn<any>;
  isDisabled: boolean;
  assistant?: AssistantCoach;
}

export const AssignmentsSection: React.FC<AssignmentsSectionProps> = ({ form, isDisabled, assistant }) => {
  const { branchOptions, regionOptions } = useSegmentation(["BRANCH", "REGION"]);

  const [openPlayerPicker, setOpenPlayerPicker] = useState(false);
  const [openAccessPlayersListDialog, setOpenAccessPlayersListDialog] = useState(false);

  const [playerSearch, setPlayerSearch] = useState("");
  const debouncedPlayerSearch = useDebounced(playerSearch, 300);

  const [playersList, setPlayersList] = useState<any[]>([]);
  const [totalPlayersCount, setTotalPlayersCount] = useState<number | null>(null);
  const [loadingPlayers, setLoadingPlayers] = useState(false);

  // For capturing the master player list from PlayerBranchList to resolve names of newly selected individual players
  const [masterPlayersList, setMasterPlayersList] = useState<any[]>([]);

  const isScoped = form.watch("isScoped");
  const userId = form.watch("userId");
  const explicitPlayerIds = form.watch("explicitPlayerIds") || [];
  const selectedBranches = form.watch("branches") || [];
  const selectedRegions = form.watch("regions") || [];

  // Determine if the scoping assignments are dirty compared to initial loaded assistant
  const isScopeDirty = useMemo(() => {
    if (!userId || !assistant) return false;

    const initialBranchIds = assistant.branches?.map(b => b.id) || [];
    const initialRegionIds = assistant.regions?.map(r => r.id) || [];
    const initialExplicitPlayerIds = assistant.explicitPlayerIds || [];

    const currentBranchIds = selectedBranches.map(Number);
    const currentRegionIds = selectedRegions.map(Number);
    const currentExplicitPlayerIds = explicitPlayerIds.map(Number);

    const branchChanged = JSON.stringify([...initialBranchIds].sort()) !== JSON.stringify([...currentBranchIds].sort());
    const regionChanged = JSON.stringify([...initialRegionIds].sort()) !== JSON.stringify([...currentRegionIds].sort());
    const explicitChanged =
      JSON.stringify([...initialExplicitPlayerIds].sort()) !== JSON.stringify([...currentExplicitPlayerIds].sort());

    return branchChanged || regionChanged || explicitChanged;
  }, [assistant, selectedBranches, selectedRegions, explicitPlayerIds, userId]);

  // Fetch active assigned players from backend if the assistant already exists and scope is not dirty
  React.useEffect(() => {
    if (!userId || isScopeDirty) {
      return;
    }

    setLoadingPlayers(true);
    AssistantCoachServices.getAssistantCoachPlayers(userId, {
      search: debouncedPlayerSearch,
      page: 1,
      perPage: 100, // Fetch up to 100 players inside the searchable modal
    })
      .then(res => {
        setPlayersList(res.content || []);
        setTotalPlayersCount(res.pagination?.totalCount ?? res.content?.length ?? 0);
      })
      .catch(err => {
        console.error("Error loading assistant coach players:", err);
      })
      .finally(() => {
        setLoadingPlayers(false);
      });
  }, [userId, debouncedPlayerSearch, isScopeDirty]);

  const displayCount = totalPlayersCount ?? 0;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-1 border-b border-slate-100 pb-3">
        <h4 className="text-xl font-bold text-slate-800">Assigned Teams & Players</h4>
        <p className="text-sm text-slate-500 leading-relaxed">
          <strong>Whom can they support?</strong> Limit this assistant's visibility to support specific branches,
          regional divisions, or individual players.
        </p>
      </div>

      {/* Conversational Access Level Picker */}
      <div className="flex flex-col gap-2">
        <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Access Scope</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div
            onClick={() => {
              if (isDisabled) return;
              form.setValue("isScoped", false, { shouldDirty: true, shouldValidate: true });
            }}
            className={cn(
              "border rounded-xl p-4 cursor-pointer transition-all flex flex-col gap-1.5",
              !isScoped
                ? "border-primary bg-blue-50/30 ring-2 ring-blue-500/20"
                : "border-slate-200 hover:border-slate-300 hover:bg-slate-50/50",
              isDisabled && "opacity-50 pointer-events-none"
            )}
          >
            <div className="flex items-center justify-between">
              <span className="font-bold text-sm text-slate-800">Full Access (Everything)</span>
              <div
                className={cn(
                  "size-4 rounded-full border flex items-center justify-center",
                  !isScoped ? "border-primary bg-primary text-white" : "border-slate-300"
                )}
              >
                {!isScoped && <div className="size-1.5 rounded-full bg-white" />}
              </div>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              This assistant can manage and support all branches, regions, and players across the company.
            </p>
          </div>

          <div
            onClick={() => {
              if (isDisabled) return;
              form.setValue("isScoped", true, { shouldDirty: true, shouldValidate: true });
            }}
            className={cn(
              "border rounded-xl p-4 cursor-pointer transition-all flex flex-col gap-1.5",
              isScoped
                ? "border-primary bg-blue-50/30 ring-2 ring-blue-500/20"
                : "border-slate-200 hover:border-slate-300 hover:bg-slate-50/50",
              isDisabled && "opacity-50 pointer-events-none"
            )}
          >
            <div className="flex items-center justify-between">
              <span className="font-bold text-sm text-slate-800">Scoped Access (Specific)</span>
              <div
                className={cn(
                  "size-4 rounded-full border flex items-center justify-center",
                  isScoped ? "border-primary bg-primary text-white" : "border-slate-300"
                )}
              >
                {isScoped && <div className="size-1.5 rounded-full bg-white" />}
              </div>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              Limit this assistant to support only selected branches, regional divisions, or individual players.
            </p>
          </div>
        </div>
      </div>

      {/* ACCESSIBLE PLAYERS SUMMARY BAR (Moved above branches scope & styled subtly) */}
      {userId && (
        <div className="flex flex-col gap-3">
          {isScopeDirty ? (
            /* Unsaved scoping changes notice */
            <div className="bg-amber-50/60 border border-amber-200/40 rounded-xl p-3.5 flex gap-3 items-center">
              <div className="size-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0 text-amber-600">
                <ShieldAlert className="size-4" />
              </div>
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-bold text-amber-800">Unsaved Scoping Changes</span>
                <p className="text-[11px] text-amber-700/90 leading-relaxed">
                  You've updated the scoping or individual assignments. Save your changes to recalculate the final
                  player list.
                </p>
              </div>
            </div>
          ) : (
            /* Static access list summary - Conversational & Inline */
            <div className="bg-slate-100/60 border border-slate-200/30 rounded-xl p-4 flex items-center gap-3">
              <div className="size-8 rounded-full bg-blue-50 flex items-center justify-center text-primary shrink-0">
                <Info className="size-4" />
              </div>
              <p className="text-xs text-slate-600 leading-relaxed">
                Based on your scoping rules, this assistant is assigned to support{" "}
                <strong className="text-slate-800 font-bold">
                  {displayCount} total {displayCount === 1 ? "player" : "players"}
                </strong>
                .{" "}
                <button
                  type="button"
                  onClick={() => setOpenAccessPlayersListDialog(true)}
                  className="text-xs font-bold text-primary hover:text-blue-800 hover:underline transition-colors focus:outline-none cursor-pointer inline-flex items-center gap-0.5"
                >
                  View Full List
                </button>
              </p>
            </div>
          )}
        </div>
      )}

      {isScoped && (
        <div className="flex flex-col gap-5 border border-slate-100 rounded-xl p-5 bg-slate-50/50">
          {/* DIVISION SCOPING SECTION (Branches & Regions) */}
          <div className="flex flex-col gap-5">
            {/* BRANCH SELECT */}
            <Controller
              name="branches"
              control={form.control}
              render={({ field, fieldState }) => {
                const selectedItems = branchOptions.filter(o => field.value?.includes(Number(o.value) || o.value));
                return (
                  <Field data-invalid={fieldState.invalid} error={fieldState.error?.message}>
                    <div className="flex flex-col gap-0.5">
                      <Field.Label className="font-bold text-slate-700">Branches Scope</Field.Label>
                      <p className="text-xs text-slate-500">Select branch teams this assistant should support</p>
                    </div>
                    <Combobox
                      items={branchOptions}
                      value={selectedItems}
                      onValueChange={selected => {
                        const values = (selected ?? []).map(o => o.value);
                        field.onChange(values);
                      }}
                      multiple
                      showClear
                      searchable
                      maxChips="auto"
                      placeholder="Filter by Branches..."
                      className="w-full bg-white border border-slate-200"
                      disabled={isDisabled}
                      itemToStringLabel={item => item.text}
                    />
                  </Field>
                );
              }}
            />

            {/* REGION SELECT */}
            <Controller
              name="regions"
              control={form.control}
              render={({ field, fieldState }) => {
                const selectedItems = regionOptions.filter(o => field.value?.includes(Number(o.value) || o.value));
                return (
                  <Field data-invalid={fieldState.invalid} error={fieldState.error?.message}>
                    <div className="flex flex-col gap-0.5">
                      <Field.Label className="font-bold text-slate-700">Regions Scope</Field.Label>
                      <p className="text-xs text-slate-500">Select regional divisions this assistant should support</p>
                    </div>
                    <Combobox
                      items={regionOptions}
                      value={selectedItems}
                      onValueChange={selected => {
                        const values = (selected ?? []).map(o => o.value);
                        field.onChange(values);
                      }}
                      multiple
                      showClear
                      searchable
                      maxChips="auto"
                      placeholder="Filter by Regions..."
                      className="w-full bg-white border border-slate-200"
                      disabled={isDisabled}
                      itemToStringLabel={item => item.text}
                    />
                  </Field>
                );
              }}
            />
          </div>

          {/* INDIVIDUAL PLAYER EXCEPTIONS SECTION */}
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <span className="text-sm font-bold text-slate-700">Individual Player Exceptions</span>
              <p className="text-xs text-slate-500 leading-relaxed">
                Utilize Branch and Region fields first to assign groups. Only assign individual players here if this
                assistant needs custom access to a specific person.
              </p>
            </div>

            {/* Selected explicit count indicator */}
            <div className="flex items-center justify-between bg-white border border-slate-200 rounded-xl p-3 shadow-sm">
              <div className="flex flex-col gap-0.5">
                <span className="text-xs font-bold text-slate-700">Individual Assignments</span>
                <span className="text-xs text-slate-400">Directly assigned players</span>
              </div>
              <div className="flex items-center gap-3">
                <Badge
                  variant="outline"
                  className="bg-slate-50 text-slate-600 font-bold border-slate-200 px-2.5 py-0.5 text-[11px]"
                >
                  {explicitPlayerIds.length} {explicitPlayerIds.length === 1 ? "Player" : "Players"} Selected
                </Badge>
                {!isDisabled && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setOpenPlayerPicker(true)}
                    className="flex items-center justify-center gap-1.5 py-1 px-3 border border-slate-200 hover:border-blue-500 hover:text-blue-600 rounded-lg text-xs font-semibold cursor-pointer transition-all"
                  >
                    <Plus className="size-3.5" />
                    <span>Manage...</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DIALOG 1: INDIVIDUAL PLAYERS ASSIGNER MODAL */}
      <Dialog open={openPlayerPicker} onOpenChange={setOpenPlayerPicker} size="xl" showCloseButton>
        <Dialog.Header className="border-b border-slate-100 pb-3">
          <Dialog.Title className="text-base font-bold text-slate-800">Assign Individual Players</Dialog.Title>
          <Dialog.Description className="text-xs text-slate-400">
            Select individual players who should be directly assigned to this assistant.
          </Dialog.Description>
        </Dialog.Header>
        <Dialog.Content className="max-h-[500px] overflow-y-auto p-4">
          <PlayersListWithBranch
            selectedPlayers={explicitPlayerIds}
            onPlayersLoaded={setMasterPlayersList}
            onChangeSelectedPlayer={(playerIds, checked) => {
              let updated;
              if (checked) {
                updated = [...new Set([...explicitPlayerIds, ...playerIds])];
              } else {
                updated = explicitPlayerIds.filter((id: number) => !playerIds.includes(id));
              }
              form.setValue("explicitPlayerIds", updated, { shouldDirty: true, shouldValidate: true });
            }}
          />
        </Dialog.Content>
        <Dialog.Footer className="bg-slate-50 border-t border-slate-100">
          <Button onClick={() => setOpenPlayerPicker(false)} className="rounded-lg text-xs py-1 px-4">
            Done
          </Button>
        </Dialog.Footer>
      </Dialog>

      {/* DIALOG 2: TOTAL ACCESSIBLE PLAYERS SEARCHABLE LIST MODAL */}
      <Dialog
        open={openAccessPlayersListDialog}
        onOpenChange={setOpenAccessPlayersListDialog}
        size="lg"
        showCloseButton
      >
        <Dialog.Header className="border-b border-slate-100 pb-3">
          <Dialog.Title className="text-base font-bold text-slate-800 flex items-center gap-2">
            <Users className="size-5 text-blue-500" />
            <span>Supported Players ({displayCount})</span>
          </Dialog.Title>
          <Dialog.Description className="text-sm text-slate-400 leading-relaxed">
            Here is the complete, searchable list of players this assistant is assigned to support based on your branch,
            region, and individual scoping rules.
          </Dialog.Description>
        </Dialog.Header>
        <Dialog.Content className="max-h-[450px] overflow-y-auto p-4 flex flex-col gap-4">
          {/* Search bar inside the Modal */}
          <div className="relative flex items-center">
            <Search className="absolute left-3 size-4 text-slate-400" />
            <Input
              placeholder="Search supported players..."
              value={playerSearch}
              onChange={e => setPlayerSearch(e.target.value)}
              className="pl-9 bg-white border border-slate-200 h-10 rounded-lg text-xs w-full"
            />
          </div>

          {/* Search result list */}
          <div className="border border-slate-200 rounded-xl overflow-hidden shadow-inner bg-slate-50/50 flex flex-col min-h-[150px]">
            <div className="max-h-[300px] overflow-y-auto divide-y divide-slate-200/60 bg-white">
              {loadingPlayers ? (
                <div className="p-10 text-center text-xs text-slate-400 font-medium">Searching players list...</div>
              ) : playersList.length > 0 ? (
                playersList.map((p, idx) => {
                  const pInfo = p.info || p.playerAccount || p;
                  const fullName =
                    pInfo?.fullName ||
                    `${pInfo?.firstName || ""} ${pInfo?.lastName || ""}`.trim() ||
                    pInfo?.email ||
                    "Unknown Player";
                  return (
                    <div
                      key={pInfo?.playerId || pInfo?.id || idx}
                      className="p-3.5 flex items-center justify-between hover:bg-slate-50/30 transition-colors"
                    >
                      <div className="flex flex-col gap-0.5">
                        <span className="text-sm font-semibold text-slate-700">{fullName}</span>
                        <span className="text-xs text-slate-400">{pInfo?.email}</span>
                      </div>
                      {p.branchNames?.length > 0 && (
                        <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                          {p.branchNames[0]}
                        </span>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="p-10 text-center text-xs text-slate-400 leading-relaxed max-w-[280px] mx-auto">
                  No players currently found matching your search.
                </div>
              )}
            </div>
          </div>
        </Dialog.Content>
        <Dialog.Footer className="bg-slate-50 border-t border-slate-100">
          <Button onClick={() => setOpenAccessPlayersListDialog(false)} className="rounded-lg text-xs py-1 px-4">
            Close
          </Button>
        </Dialog.Footer>
      </Dialog>
    </div>
  );
};
export default AssignmentsSection;
